import {
  ETIQUETA_NOVEDAD_MINEROS,
  ETIQUETA_NOVEDAD_MINEROS_EN,
  ETIQUETA_TENOR_MINEROS,
  ETIQUETA_TENOR_MINEROS_EN,
  TENORES_DE_MINEROS,
  TIPOS_DE_NOVEDAD_MINEROS,
  type BolsaDeMineros,
  type CifrasDeMineros,
  type EtapaDeMineros,
  type ResumenDeMineros,
} from '@cnc/contracts';
import {Fragment, useEffect, useState, type ComponentProps, type ReactNode} from 'react';
import {Link, useLocation, useSearchParams} from 'react-router';
import {
  rutaExcelDeMineros,
  useBolsasDeMineros,
  useCargarDeclaracionesDeMineros,
  useCargarMineros,
  useResumenDeMineros,
} from '../api/mineros';
import {AYUDA_MINEROS as AYUDA} from '../componentes/ayuda-mineros';
import {ChipsDeTotales} from '../componentes/chips-de-totales';
import {BarraDeVistas} from '../componentes/barra-de-vistas';
import {DEFINICION_MINEROS, ordenarMineros} from '../componentes/columnas-mineros';
import {ElegirArchivo} from '../componentes/elegir-archivo';
import {InformeDeInventarioMineros} from '../componentes/informe-de-inventario-mineros';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {Paginacion} from '../componentes/paginacion';
import {EncabezadoInventario} from '../componentes/encabezado-inventario';
import {MenuDeHerramientas, OPCION_DE_MENU} from '../componentes/menu-de-herramientas';
import {RangoDeLlegada, RangoDeZarpe} from '../componentes/rango-de-llegada';
import {SelectorDeColumnas} from '../componentes/selector-de-columnas';
import {SelectorMultiple} from '../componentes/selector-multiple';
import {usePermisos} from '../componentes/sesion-contexto';
import {Tabla} from '../componentes/tabla';
import {useColumnas} from '../componentes/usar-columnas';
import {ZonaAmpliable} from '../componentes/zona-ampliable';
import {et, tr, trx, txt, type Texto} from '../i18n/idioma';
import {chipsDeTenor} from '../utilidades/tenor';
import {fecha, fechaHora, mes, numero} from '../utilidades/formato';

// Inventario → Mineros (decisiones 0022 y 0052): los Big Bags de la planta de
// Mineros en El Bagre, su paso por la bodega de Cartagena y su exportacion. La
// linea de etapas es a la vez la barra de subpestanas (Resumen = todas). Debajo,
// la misma tabla del Consolidado: columnas elegibles, orden, arrastre, totales y
// paginas (Daniel, 13/10/2026). Todo se calcula en la fecha de corte y por tenor.
const ANTES_DEL_PUERTO: EtapaDeMineros[] = ['planta', 'camino', 'bodega'];
// Nombre corto de cada etapa en su pestana (el completo sale al pasar el mouse).
const ETAPA_CORTA: Record<EtapaDeMineros, Texto> = {
  planta: txt('El Bagre', 'El Bagre'),
  camino: txt('Camino', 'On the way'),
  bodega: txt('Bodega', 'Warehouse'),
  puerto: txt('Puerto', 'Port'),
  navegando: txt('Navegando', 'At sea'),
  llego: txt('Llegó', 'Arrived'),
  entregado: txt('Entregado', 'Delivered'),
};
// Lo que solo ordena o pagina la tabla en la pantalla: no va a la API.
const SOLO_PANTALLA = ['orden', 'direccion', 'pagina', 'porPagina'];

export function PaginaMineros() {
  const [parametros, setParametros] = useSearchParams();
  const corte = parametros.get('corte');
  const tenor = parametros.get('tenor');
  const etapa = parametros.get('etapa');
  const pestana = !etapa || etapa === 'todos' ? 'resumen' : etapa;
  const resumen = useResumenDeMineros(corte, tenor);
  const consulta = new URLSearchParams(parametros);
  if (pestana === 'resumen') consulta.delete('etapa');
  for (const clave of SOLO_PANTALLA) consulta.delete(clave);
  const bolsas = useBolsasDeMineros(consulta);
  const permisos = usePermisos();
  const [columnas, cambiarColumnas] = useColumnas(DEFINICION_MINEROS);
  const [cargaAbierta, setCargaAbierta] = useState(false);
  const [declaracionesAbiertas, setDeclaracionesAbiertas] = useState(false);
  const [informeAbierto, setInformeAbierto] = useState(false);
  const cambiar = (clave: string, valor: string | undefined, quitar: string[] = []) => {
    const nuevos = new URLSearchParams(window.location.search);
    if (valor) nuevos.set(clave, valor);
    else nuevos.delete(clave);
    for (const q of quitar) nuevos.delete(q);
    // Cambiar un filtro vuelve a la primera pagina.
    if (clave !== 'pagina') nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  const r = resumen.data;
  const tenorEt = et(ETIQUETA_TENOR_MINEROS, ETIQUETA_TENOR_MINEROS_EN);

  // Orden y paginas en la pantalla (la API trae todas las bolsas del filtro).
  const orden = parametros.get('orden') ?? 'bbNo';
  const direccion = parametros.get('direccion') === 'desc' ? 'desc' : 'asc';
  const porPagina = Number(parametros.get('porPagina') ?? 100) || 100;
  const elementos = bolsas.data?.elementos ?? [];
  const ordenadas = parametros.get('orden') ? ordenarMineros(elementos, orden, direccion) : elementos;
  const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / porPagina));
  const pagina = Math.min(Math.max(1, Number(parametros.get('pagina') ?? 1) || 1), totalPaginas);
  const filas = ordenadas.slice((pagina - 1) * porPagina, pagina * porPagina);
  const ordenar = (columna: string) => {
    const nuevos = new URLSearchParams(window.location.search);
    nuevos.set('direccion', orden === columna && direccion === 'asc' ? 'desc' : 'asc');
    nuevos.set('orden', columna);
    setParametros(nuevos, {replace: true});
  };

  return (
    <section className="space-y-4">
      <EncabezadoInventario
        descripcion={tr(
          'Big Bags de Mineros desde la planta de El Bagre hasta su destino. Se lleva aparte del inventario de CNC.',
          'Mineros Big Bags from the El Bagre plant to their destination. Kept apart from the CNC inventory.',
        )}
        herramientas={
          // Como en el Consolidado: descargar, cargar, ver en la tabla, actualizar y columnas (Daniel, 13/10/2026).
          <MenuDeHerramientas>
            {cerrar => (
              <>
                <a href={rutaExcelDeMineros(consulta)} onClick={cerrar} className={OPCION_DE_MENU}>
                  {tr('⬇ Excel', '⬇ Excel')}
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setInformeAbierto(x => !x);
                    cerrar();
                  }}
                  className={OPCION_DE_MENU}
                >
                  {tr('⬇ Informe de inventario de concentrados', '⬇ Concentrates inventory report')}
                </button>
                {permisos.editar && (
                  <button
                    type="button"
                    onClick={() => {
                      setCargaAbierta(x => !x);
                      cerrar();
                    }}
                    className={OPCION_DE_MENU}
                  >
                    {tr('⬆ Cargar archivos de Mineros', '⬆ Load Mineros files')}
                  </button>
                )}
                {permisos.editar && (
                  <button
                    type="button"
                    onClick={() => {
                      setDeclaracionesAbiertas(x => !x);
                      cerrar();
                    }}
                    className={OPCION_DE_MENU}
                  >
                    {tr('⬆ Cargar declaraciones de valor (TM)', '⬆ Load value declarations (TM)')}
                  </button>
                )}
                <Link to="/inventario?linea=mineros" className={OPCION_DE_MENU}>
                  {tr('Ver en la tabla del inventario', 'See in the inventory table')}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    void resumen.refetch();
                    void bolsas.refetch();
                    cerrar();
                  }}
                  className={OPCION_DE_MENU}
                >
                  {tr('↻ Actualizar', '↻ Refresh')}
                </button>
                <SelectorDeColumnas
                  definicion={DEFINICION_MINEROS}
                  visibles={columnas}
                  onCambiar={cambiarColumnas}
                  claseDelBoton={OPCION_DE_MENU}
                />
              </>
            )}
          </MenuDeHerramientas>
        }
      />
      {informeAbierto && r && <InformeDeInventarioMineros hoy={r.hoy} onCerrar={() => setInformeAbierto(false)} />}
      {permisos.editar && cargaAbierta && <CargaDeMineros abiertaAlInicio onCerrar={() => setCargaAbierta(false)} />}
      {permisos.editar && declaracionesAbiertas && (
        <CargaDeDeclaracionesDeMineros onCerrar={() => setDeclaracionesAbiertas(false)} />
      )}

      {/* Vistas guardadas (Daniel, 15/10/2026): pestaña, filtros, orden y columnas. */}
      <BarraDeVistas
        pantalla="mineros"
        actual={{parametros: parametros.toString(), columnas, extra: {}}}
        onAplicar={configuracion => {
          setParametros(new URLSearchParams(configuracion.parametros), {replace: true});
          if (configuracion.columnas) cambiarColumnas(configuracion.columnas);
        }}
      />
      {r && <FechasDeLosArchivos r={r} />}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-linea bg-white px-3 py-2 text-sm">
        <label className="flex flex-col gap-1 text-xs text-apagado">
          <span>
            {tr('Fecha de corte', 'Cut-off date')}
            <NotaDeAyuda texto={AYUDA.corte!} dentroDeEtiqueta />
          </span>
          <input
            type="date"
            value={r?.corte ?? corte ?? ''}
            onChange={e => cambiar('corte', e.target.value || undefined)}
            className="rounded-md border border-linea px-2 py-1 text-sm text-tinta"
          />
        </label>
        <div className="flex flex-wrap gap-1 pb-0.5">
          <Chip activo={!corte} onClick={() => cambiar('corte', undefined)}>
            {tr('Hoy', 'Today')}
          </Chip>
          {(r?.meses ?? [])
            .filter(m => m.mes < (r?.hoy ?? '').slice(0, 7))
            .map(m => {
              const fin = finDeMes(m.mes);
              return (
                <Chip key={m.mes} activo={corte === fin} onClick={() => cambiar('corte', fin)}>
                  {tr(`Cierre de ${mes(m.mes)}`, `End of ${mes(m.mes)}`)}
                </Chip>
              );
            })}
        </div>
        <div className="flex flex-col gap-1 text-xs text-apagado" role="group" aria-label={tr('Tenor', 'Grade')}>
          <span>
            {tr('Tenor', 'Grade')}
            <NotaDeAyuda titulo={txt('Tenor', 'Grade')} texto={AYUDA.filtroTenor!} />
          </span>
          <div className="flex gap-1">
            <Chip activo={!tenor} onClick={() => cambiar('tenor', undefined)}>
              {tr('Todos', 'All')}
            </Chip>
            {TENORES_DE_MINEROS.map(t => (
              <Chip key={t} activo={tenor === t} onClick={() => cambiar('tenor', t)}>
                {tenorEt[t]}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      {resumen.isError && <p className="text-sm text-alerta">{resumen.error.message}</p>}
      {r && (
        <LineaDeEtapas
          r={r}
          activa={pestana}
          elegir={p => cambiar('etapa', p === 'resumen' ? undefined : p, ['asignacion'])}
        />
      )}

      {pestana === 'resumen' && r && (
        <div className="space-y-2">
          <ResumenCompacto r={r} novedad={parametros.get('novedad')} elegir={v => cambiar('novedad', v)} />
          {r.meses.length > 0 && (
            <details className="rounded-lg border border-linea bg-white text-sm">
              <summary className="cursor-pointer px-3 py-1.5 text-xs text-apagado hover:text-tinta">
                {tr(
                  'Por mes: salió de El Bagre, llegó a Cartagena, salió de la bodega y lo que quedaba al cierre',
                  'By month: left El Bagre, arrived in Cartagena, left the warehouse and what was left at close',
                )}
              </summary>
              <PorMes r={r} />
            </details>
          )}
        </div>
      )}

      <ZonaAmpliable titulo={tr('Inventario de Mineros', 'Mineros inventory')}>
        {pestana === 'bodega' && (
          <div
            className="flex flex-wrap items-center gap-1 text-sm"
            role="group"
            aria-label={tr('Asignación', 'Assignment')}
          >
            <NotaDeAyuda titulo={txt('Asignación', 'Assignment')} texto={AYUDA.filtroAsignacion!} />
            <Chip activo={!parametros.get('asignacion')} onClick={() => cambiar('asignacion', undefined)}>
              {tr('Todos', 'All')}
            </Chip>
            <Chip
              activo={parametros.get('asignacion') === 'disponible'}
              onClick={() => cambiar('asignacion', 'disponible')}
            >
              {tr('Disponibles', 'Available')}
            </Chip>
            <Chip
              activo={parametros.get('asignacion') === 'asignada'}
              onClick={() => cambiar('asignacion', 'asignada')}
            >
              {tr('Asignados a una exportación', 'Assigned to an export')}
            </Chip>
          </div>
        )}
        <FiltrosDeMineros
          parametros={parametros}
          cambiar={cambiar}
          opciones={bolsas.data}
          r={r}
          limpiar={() => {
            const nuevos = new URLSearchParams();
            for (const clave of ['corte', 'tenor', 'etapa', 'asignacion', ...SOLO_PANTALLA]) {
              const v = parametros.get(clave);
              if (v && clave !== 'pagina') nuevos.set(clave, v);
            }
            setParametros(nuevos, {replace: true});
          }}
        />
        {bolsas.isError && <p className="text-sm text-alerta">{bolsas.error.message}</p>}
        {bolsas.data && <ChipsDeTotales datos={datosDeTotales(bolsas.data.totales)} prueba="totales-mineros" />}
        <TablaDeMineros
          filas={filas}
          columnas={columnas}
          orden={orden}
          direccion={direccion}
          onOrdenar={ordenar}
          onReordenar={cambiarColumnas}
          cargando={bolsas.isFetching}
          numeracionDesde={(pagina - 1) * porPagina}
        />
        <Paginacion
          pagina={pagina}
          totalPaginas={totalPaginas}
          porPagina={porPagina}
          total={ordenadas.length}
          onPagina={p => cambiar('pagina', String(p))}
          onPorPagina={n => cambiar('porPagina', String(n))}
        />
      </ZonaAmpliable>
    </section>
  );
}

const finDeMes = (m: string) => {
  const [a, n] = m.split('-').map(Number);
  return new Date(Date.UTC(a!, n!, 0)).toISOString().slice(0, 10);
};

// Los mismos totales del Consolidado, en una linea.
const datosDeTotales = (c: CifrasDeMineros): Array<[string, string, string?]> => [
  [
    'Big Bags',
    numero(c.bigBags),
    c.incompletos ? tr(`${numero(c.incompletos)} incompletos`, `${numero(c.incompletos)} incomplete`) : undefined,
  ],
  [tr('Ton. húmedas', 'Wet tons'), numero(c.tonHumedas, 3)],
  [tr('Ton. secas', 'Dry tons'), numero(c.tonSecas, 3)],
  ['Au (g)', numero(c.auG, 2)],
  ['Ag (g)', numero(c.agG, 2)],
  ['Au (oz)', numero(c.auOz, 3)],
  ['Ag (oz)', numero(c.agOz, 3)],
  ...chipsDeTenor(c),
];

// La tabla comun (la del Consolidado) con las columnas de Mineros. El BB # lleva
// a su ficha; la exportacion, al seguimiento; en amarillo, oro distinto al documento.
function TablaDeMineros(props: {
  filas: BolsaDeMineros[];
  columnas: string[];
  orden: string;
  direccion: 'asc' | 'desc';
  onOrdenar: (orden: string) => void;
  onReordenar: (ids: string[]) => void;
  cargando: boolean;
  numeracionDesde: number;
}) {
  const {pathname, search} = useLocation();
  return (
    <div data-prueba="mineros-bolsas">
      <Tabla
        filas={props.filas}
        definicion={DEFINICION_MINEROS}
        columnasVisibles={props.columnas}
        claveDeFila={b => b.bbNo}
        orden={props.orden}
        direccion={props.direccion}
        onOrdenar={props.onOrdenar}
        onReordenar={props.onReordenar}
        cargando={props.cargando}
        numeracionDesde={props.numeracionDesde}
        mensajeVacio={tr('No hay Big Bags de Mineros con estos filtros.', 'No Mineros Big Bags with these filters.')}
        claseDeFila={b => (b.oroDistinto ? 'bg-amber-50' : undefined)}
        celda={(columna, b) => {
          if (columna.id === 'bbNo')
            return (
              <Link
                to={`/inventario/${encodeURIComponent(b.bbNo)}`}
                state={{volver: `${pathname}${search}`}}
                className="font-medium text-verde hover:underline"
              >
                {b.bbNo}
              </Link>
            );
          if (columna.id === 'exportacion' && b.exportacion)
            return (
              <Link to={`/seguimiento/${encodeURIComponent(b.exportacion)}`} className="text-verde hover:underline">
                {b.exportacion}
              </Link>
            );
          if (columna.id === 'novedades' && b.novedades.length)
            return (
              <span title={b.novedades.join('\n')} className="text-xs">
                ⚠ {columna.valor(b)}
              </span>
            );
          if (columna.id === 'auG' && b.oroDistinto)
            return (
              <span
                className="rounded bg-amber-100 px-1"
                title={tr(
                  `Documento de exportación: ${numero(b.auGDocumento, 2)} g`,
                  `Export document: ${numero(b.auGDocumento, 2)} g`,
                )}
              >
                {columna.valor(b)}
              </span>
            );
          return undefined;
        }}
      />
    </div>
  );
}

// Filtros como los del Consolidado: buscar mientras se escribe, los principales
// arriba y "Más filtros" con los demas.
function FiltrosDeMineros({
  parametros,
  cambiar,
  opciones,
  r,
  limpiar,
}: {
  parametros: URLSearchParams;
  cambiar: (clave: string, valor: string | undefined) => void;
  opciones: {tms: string[]; exportaciones: string[]} | undefined;
  r: ResumenDeMineros | undefined;
  limpiar: () => void;
}) {
  const buscar = parametros.get('buscar') ?? '';
  const [busqueda, setBusqueda] = useState(buscar);
  useEffect(() => {
    const t = setTimeout(() => {
      if (busqueda.trim() !== buscar) cambiar('buscar', busqueda.trim() || undefined);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda]);
  useEffect(() => setBusqueda(buscar), [buscar]);
  const SECUNDARIOS = ['etdDesde', 'etdHasta', 'etaDesde', 'etaHasta', 'oroDistinto', 'novedades'];
  const ocultos = SECUNDARIOS.filter(c => parametros.get(c)).length;
  const [mas, setMas] = useState(ocultos > 0);
  const activos = ['buscar', 'exportacion', 'tm', 'novedad', ...SECUNDARIOS].filter(c => parametros.get(c)).length;
  const novedad = et(ETIQUETA_NOVEDAD_MINEROS, ETIQUETA_NOVEDAD_MINEROS_EN);
  return (
    <div className="rounded-lg border border-linea bg-white px-3 py-2">
      <div className="grid items-end gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        <label className="block text-xs text-apagado sm:col-span-2">
          {tr('Buscar', 'Search')}
          <NotaDeAyuda texto={AYUDA.buscar!} dentroDeEtiqueta />
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={tr('BB #, sello, contenedor, lote o exportación', 'BB #, seal, container, lot or export')}
            className="mt-0.5 w-full rounded-md border border-linea px-3 py-1 text-sm text-tinta"
          />
        </label>
        <SelectorMultiple
          etiqueta={tr('Exportación', 'Export')}
          valor={parametros.get('exportacion') ?? undefined}
          opciones={[
            {valor: 'SIN', etiqueta: tr('Sin exportación', 'No export')},
            ...(opciones?.exportaciones ?? []).map(x => ({valor: x, etiqueta: x})),
          ]}
          onCambiar={v => cambiar('exportacion', v)}
          ayuda={AYUDA.filtroExportacion}
        />
        <SelectorMultiple
          etiqueta={tr('Recolección (TM)', 'Pickup (TM)')}
          valor={parametros.get('tm') ?? undefined}
          opciones={[
            {valor: 'sin', etiqueta: tr('Sin declaración de valor', 'No value declaration')},
            ...(opciones?.tms ?? []).map(x => ({valor: x, etiqueta: x})),
          ]}
          onCambiar={v => cambiar('tm', v)}
          ayuda={AYUDA.filtroTm}
        />
        <SelectorMultiple
          etiqueta={tr('Novedad', 'Issue')}
          valor={parametros.get('novedad') ?? undefined}
          opciones={TIPOS_DE_NOVEDAD_MINEROS.map(t => ({valor: t, etiqueta: novedad[t]}))}
          onCambiar={v => cambiar('novedad', v)}
          ayuda={AYUDA.filtroNovedad}
        />
        <button
          type="button"
          aria-expanded={mas}
          onClick={() => setMas(v => !v)}
          className="rounded-md border border-linea bg-white px-2 py-1 text-xs text-tinta hover:bg-papel"
        >
          {mas ? tr('▴ Menos filtros', '▴ Fewer filters') : tr('▾ Más filtros', '▾ More filters')}
          {ocultos > 0 && <span className="ml-1 font-semibold text-verde-oscuro">({ocultos})</span>}
        </button>
      </div>
      {mas && (
        <div className="mt-2 flex flex-wrap items-end gap-3 border-t border-linea/70 pt-2 text-sm">
          <RangoDeZarpe parametros={parametros} cambiar={cambiar} />
          <span className="pb-2">
            <NotaDeAyuda titulo={txt('Mes de zarpe (ETD)', 'Departure month (ETD)')} texto={AYUDA.filtroZarpe!} />
          </span>
          <RangoDeLlegada parametros={parametros} cambiar={cambiar} />
          <span className="pb-2">
            <NotaDeAyuda titulo={txt('Llegada (ETA)', 'Arrival (ETA)')} texto={AYUDA.filtroLlegada!} />
          </span>
          <label className="flex items-center gap-2 pb-1">
            <input
              type="checkbox"
              checked={parametros.get('novedades') === 'true'}
              onChange={e => cambiar('novedades', e.target.checked ? 'true' : undefined)}
            />
            {tr(
              `Solo con novedades${r ? ` (${r.conNovedades})` : ''}`,
              `Only with issues${r ? ` (${r.conNovedades})` : ''}`,
            )}
            <NotaDeAyuda texto={AYUDA.soloNovedades!} dentroDeEtiqueta />
          </label>
          <label className="flex items-center gap-2 pb-1">
            <input
              type="checkbox"
              checked={parametros.get('oroDistinto') === 'true'}
              onChange={e => cambiar('oroDistinto', e.target.checked ? 'true' : undefined)}
            />
            <span className="rounded bg-amber-50 px-1">
              {tr(
                `Oro distinto al documento de exportación${r ? ` (${r.conOroDistinto})` : ''}`,
                `Gold differs from the export document${r ? ` (${r.conOroDistinto})` : ''}`,
              )}
            </span>
            <NotaDeAyuda texto={AYUDA.filtroOroDistinto!} dentroDeEtiqueta />
          </label>
        </div>
      )}
      {activos > 0 && (
        <div className="mt-2 flex items-center justify-between text-xs text-apagado">
          <span>
            {tr(
              `${activos} filtro${activos === 1 ? '' : 's'} activo${activos === 1 ? '' : 's'}`,
              `${activos} active filter${activos === 1 ? '' : 's'}`,
            )}
          </span>
          <button type="button" onClick={limpiar} className="text-verde hover:underline">
            {tr('Quitar filtros', 'Clear filters')}
          </button>
        </div>
      )}
    </div>
  );
}

// Resumen en una linea: lo disponible y las novedades por tipo (clic filtra la tabla).
function ResumenCompacto({
  r,
  novedad,
  elegir,
}: {
  r: ResumenDeMineros;
  novedad: string | null;
  elegir: (tipo: string | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-prueba="mineros-novedades">
      <span className="rounded-md border border-verde bg-verde-claro/40 px-2 py-0.5" data-prueba="mineros-disponible">
        {tr('Disponible sin exportación', 'Available without export')}{' '}
        <b className="text-sm tabular-nums text-verde-oscuro">{numero(r.disponible.bigBags)}</b>
        <span className="ml-1 tabular-nums text-apagado">
          ({numero(r.disponible.tonSecas, 1)} t · {numero(r.disponible.auOz, 1)} oz)
        </span>
        <NotaDeAyuda titulo={txt('Disponible sin exportación', 'Available without export')} texto={AYUDA.disponible!} />
      </span>
      <span className="ml-2 text-apagado">
        {r.novedades.length ? tr('Novedades para revisar:', 'Issues to review:') : tr('Sin novedades.', 'No issues.')}
      </span>
      {r.novedades.length > 0 && <NotaDeAyuda titulo={txt('Novedades', 'Issues')} texto={AYUDA.novedadesResumen!} />}
      {r.novedades.map(n => (
        <button
          key={n.tipo}
          type="button"
          aria-pressed={novedad === n.tipo}
          onClick={() => elegir(novedad === n.tipo ? undefined : n.tipo)}
          title={n.accion}
          className={`rounded-md border px-2 py-0.5 ${novedad === n.tipo ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white hover:bg-papel'}`}
        >
          {n.etiqueta} <b className="tabular-nums">{numero(n.bigBags)}</b>
        </button>
      ))}
    </div>
  );
}

function Chip({activo, onClick, children}: {activo: boolean; onClick: () => void; children: ReactNode}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={`rounded-full border px-2.5 py-0.5 text-xs ${activo ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white text-tinta hover:bg-papel'}`}
    >
      {children}
    </button>
  );
}

// Hasta donde llegan los datos de cada archivo. Sin alarmas: actualizarlos
// depende de quien los cargue (Daniel, 12/10/2026).
function FechasDeLosArchivos({r}: {r: ResumenDeMineros}) {
  if (!r.ultimaCarga) return null;
  const c = r.ultimaCarga;
  return (
    <p className="text-xs text-apagado" data-prueba="mineros-archivos">
      {tr(
        `Planta: ${c.archivo ?? '—'}, último despacho registrado el ${fecha(r.ultimoDespachoDePlanta)}. Bodega: ${c.bodega ?? '—'}, último movimiento registrado el ${fecha(r.ultimoMovimientoDeBodega)}. Cargados el ${fechaHora(c.fecha)}.`,
        `Plant: ${c.archivo ?? '—'}, last shipment recorded on ${fecha(r.ultimoDespachoDePlanta)}. Warehouse: ${c.bodega ?? '—'}, last movement recorded on ${fecha(r.ultimoMovimientoDeBodega)}. Loaded on ${fechaHora(c.fecha)}.`,
      )}
      <NotaDeAyuda titulo={txt('Archivos cargados', 'Loaded files')} texto={AYUDA.archivos!} />
    </p>
  );
}

// La linea de etapas: suma el total, en el orden en que se mueve la carga. Cada
// etapa es una subpestana; Resumen (todas) va primero.
function LineaDeEtapas({r, activa, elegir}: {r: ResumenDeMineros; activa: string; elegir: (p: string) => void}) {
  // Pestanas compactas (Daniel, 12/10/2026): nombre y Big Bags; el detalle va en una linea chica y al pasar el mouse.
  const clase = (p: string) =>
    `flex flex-col rounded-md border px-2 py-1 text-left text-sm leading-tight ${activa === p ? 'border-verde bg-verde-claro/60 text-verde-oscuro' : 'border-linea bg-white hover:bg-papel'}`;
  return (
    <nav
      aria-label={tr('Etapas de Mineros', 'Mineros stages')}
      data-prueba="mineros-etapas"
      className="flex flex-wrap items-center gap-x-0.5 gap-y-1"
    >
      <button
        type="button"
        aria-current={activa === 'resumen' ? 'page' : undefined}
        onClick={() => elegir('resumen')}
        className={`${clase('resumen')} mr-3`}
      >
        <span>
          <span className="font-medium">{tr('Resumen', 'Summary')}</span>{' '}
          <b className="tabular-nums">{numero(r.total.bigBags)}</b>
        </span>
      </button>

      {r.porEtapa.map((e, i) => (
        <Fragment key={e.etapa}>
          {i > 0 && (
            <span aria-hidden className="text-xs text-apagado">
              →
            </span>
          )}
          <button
            type="button"
            aria-current={activa === e.etapa ? 'page' : undefined}
            onClick={() => elegir(e.etapa)}
            className={clase(e.etapa)}
            data-prueba={`mineros-etapa-${e.etapa}`}
            title={tr(
              `${e.etiqueta}: ${numero(e.cifras.bigBags)} Big Bags · ${numero(e.cifras.auOz, 1)} oz Au · alto tenor ${numero(e.alto.bigBags)} · bajo tenor ${numero(e.bajo.bigBags)}`,
              `${e.etiqueta}: ${numero(e.cifras.bigBags)} Big Bags · ${numero(e.cifras.auOz, 1)} oz Au · high grade ${numero(e.alto.bigBags)} · low grade ${numero(e.bajo.bigBags)}`,
            )}
          >
            <span>
              {trx(ETAPA_CORTA[e.etapa])} <b className="tabular-nums">{numero(e.cifras.bigBags)}</b>
            </span>
            {ANTES_DEL_PUERTO.includes(e.etapa) && e.cifras.bigBags > 0 && (
              <span className="text-[11px] text-apagado tabular-nums">
                {tr(
                  `${numero(e.disponibles.bigBags)} disp. · ${numero(e.asignadas.bigBags)} asig.`,
                  `${numero(e.disponibles.bigBags)} avail. · ${numero(e.asignadas.bigBags)} assig.`,
                )}
              </span>
            )}
          </button>
        </Fragment>
      ))}
      <NotaDeAyuda titulo={txt('Etapas de Mineros', 'Mineros stages')} texto={AYUDA.etapas!} />
    </nav>
  );
}

// Por mes: que salio de El Bagre, que llego a Cartagena, que salio de la bodega
// y que quedaba al cierre.
function PorMes({r}: {r: ResumenDeMineros}) {
  const celda = (c: CifrasDeMineros, extra?: string) => (
    <td className="px-3 py-1 text-right tabular-nums">
      {numero(c.bigBags)} <span className="text-xs text-apagado">· {numero(c.auOz, 1)} oz</span>
      {extra && <span className="ml-1 text-xs text-apagado">({extra})</span>}
    </td>
  );
  return (
    <div className="overflow-x-auto border-t border-linea" data-prueba="mineros-corte-mensual">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-apagado">
            <th className="px-3 py-2 font-semibold">
              {tr('Mes', 'Month')}
              <NotaDeAyuda texto={AYUDA.mes!} />
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {tr('Salió de El Bagre', 'Left El Bagre')}
              <NotaDeAyuda texto={AYUDA.despachado!} />
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {tr('Llegó a Cartagena', 'Arrived in Cartagena')}
              <NotaDeAyuda texto={AYUDA.ingresado!} />
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {tr('Salió de la bodega (exportado)', 'Left the warehouse (exported)')}
              <NotaDeAyuda texto={AYUDA.llenado!} />
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {tr('Al cierre: planta y camino', 'At close: plant and on the way')}
              <NotaDeAyuda texto={AYUDA.enPlantaOCamino!} />
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {tr('Al cierre: bodega', 'At close: warehouse')}
              <NotaDeAyuda texto={AYUDA.enBodega!} />
            </th>
          </tr>
        </thead>
        <tbody>
          {r.meses.map(m => (
            <tr key={m.mes} className="border-t border-linea/70">
              <td className="px-3 py-1 font-medium">{mes(m.mes)}</td>
              {celda(m.despachado)}
              {celda(m.ingresado)}
              {celda(m.llenado)}
              {celda(m.enPlantaOCamino)}
              {celda(
                m.enBodega,
                tr(
                  `${numero(m.enBodegaAsignadas.bigBags)} asignados`,
                  `${numero(m.enBodegaAsignadas.bigBags)} assigned`,
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-3 pb-2 text-xs text-apagado">
        {tr(
          'Big Bags y onzas de oro de cada mes. "Salió de la bodega" es el llenado del contenedor: ahí pasa a exportado. El mes del corte va hasta la fecha de corte. Los Big Bags con lotes sin datos cuentan como bolsa pero no suman onzas.',
          'Big Bags and gold ounces for each month. "Left the warehouse" is the container filling: that is when it becomes exported. The cut-off month runs up to the cut-off date. Big Bags with lots missing data count as bags but add no ounces.',
        )}
      </p>
    </div>
  );
}

// Cargar los archivos: el BD Envios de la planta (el de numero mas alto), la
// base de la bodega y, si hay nuevas, las listas de empaque de las exportaciones.
// Tambien se usa en Cargar archivos, ya abierta.
export function CargaDeMineros({
  abiertaAlInicio = false,
  onCerrar,
}: {
  abiertaAlInicio?: boolean;
  // Si quien la muestra decide cuando cerrarla (el boton esta afuera).
  onCerrar?: () => void;
}) {
  const [abierta, setAbierta] = useState(abiertaAlInicio);
  const [envios, setEnvios] = useState<File | null>(null);
  const [bodega, setBodega] = useState<File | null>(null);
  const [listas, setListas] = useState<File[]>([]);
  const [declaraciones, setDeclaraciones] = useState<File[]>([]);
  const cargar = useCargarMineros();
  const r = cargar.data;
  const enviar = (modo: 'revisar' | 'aplicar') =>
    envios && cargar.mutate({envios, bodega, listas, declaraciones, modo});
  if (!abierta)
    return (
      <button
        type="button"
        onClick={() => setAbierta(true)}
        className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
      >
        {tr('⬆ Cargar archivos de Mineros', '⬆ Load Mineros files')}
      </button>
    );
  return (
    <div className="space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="carga-mineros">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Cargar archivos de Mineros', 'Load Mineros files')}</h2>
          <p className="text-xs text-apagado">
            {tr(
              'Basta el DB Mineros Consolidated más reciente: trae la planta, la bodega y la exportación de cada Big Bag. Si en su lugar usas el BD Envíos de la planta (el de número más alto), elige también la base de la bodega de Cartagena. Las listas de empaque (Calculos Expo concentrados MA0XX o, si aún no está, Expo MA0XX) asignan los Big Bags a su exportación; con el consolidado, su columna Export ID asigna los que no están en ninguna lista. Los demás conservan la exportación que tengan. Primero se revisa y nada se guarda hasta que confirmes.',
              'The latest DB Mineros Consolidated is enough: it has the plant, the warehouse and the export of each Big Bag. If you use the plant BD Envíos (highest number) instead, also choose the Cartagena warehouse database. The packing lists (Calculos Expo concentrados MA0XX or, if not there yet, Expo MA0XX) assign the Big Bags to their export; with the consolidated file, its Export ID column assigns those in no list. The rest keep their current export. It is reviewed first and nothing is saved until you confirm.',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => (onCerrar ? onCerrar() : setAbierta(false))}
          aria-label={tr('Cerrar', 'Close')}
          className="text-apagado hover:text-tinta"
        >
          ×
        </button>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <CampoDeArchivoConAyuda
          ayuda={AYUDA.archivoEnvios!}
          etiqueta={tr('BD Envíos Mineros Aluvial (.xlsx)', 'BD Envíos Mineros Aluvial (.xlsx)')}
          accept=".xlsx"
          archivos={envios ? [envios] : []}
          onElegir={([a]) => {
            setEnvios(a ?? null);
            cargar.reset();
          }}
        />
        <CampoDeArchivoConAyuda
          ayuda={AYUDA.archivoBodega!}
          etiqueta={tr('Base de la bodega (Big Bags exportación)', 'Warehouse database (Big Bags export)')}
          accept=".xlsx"
          archivos={bodega ? [bodega] : []}
          onElegir={([a]) => {
            setBodega(a ?? null);
            cargar.reset();
          }}
        />
        <CampoDeArchivoConAyuda
          ayuda={AYUDA.archivoListas!}
          etiqueta={tr('Listas de empaque (opcional, varias)', 'Packing lists (optional, several)')}
          accept=".xlsx"
          multiple
          archivos={listas}
          onElegir={a => {
            setListas(a);
            cargar.reset();
          }}
        />
        <CampoDeArchivoConAyuda
          ayuda={AYUDA.archivoDeclaraciones!}
          etiqueta={tr(
            'Declaraciones de valor TM (opcional, PDF, varias)',
            'TM value declarations (optional, PDF, several)',
          )}
          accept=".pdf"
          multiple
          archivos={declaraciones}
          onElegir={a => {
            setDeclaraciones(a);
            cargar.reset();
          }}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!envios || cargar.isPending}
          onClick={() => enviar('revisar')}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
        >
          {tr('Revisar', 'Review')}
        </button>
        {r && !r.aplicado && (
          <button
            type="button"
            disabled={cargar.isPending}
            onClick={() => enviar('aplicar')}
            className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {tr('Confirmar y guardar', 'Confirm and save')}
          </button>
        )}
        {cargar.isPending && <span className="text-sm text-apagado">{tr('Leyendo…', 'Reading…')}</span>}
      </div>
      {cargar.isError && <p className="text-sm text-alerta">{cargar.error.message}</p>}
      {r && (
        <div className="space-y-1 text-sm" role="status">
          <p className="font-medium">
            {r.aplicado
              ? tr('Guardado.', 'Saved.')
              : tr('Revisión (todavía no se guardó nada):', 'Review (nothing saved yet):')}
          </p>
          <p>
            {tr(
              `${numero(r.bigBags)} Big Bags (${numero(r.bigBagsNuevos)} nuevos), ${numero(r.lotes)} lotes y ${numero(r.tulas)} tulas. ${numero(r.completos)} completos y ${numero(r.incompletos)} con lotes sin datos. ${numero(r.bolsasConTm)} con su recolección TM (${numero(r.recoleccionesTm)} TM). ${numero(r.conNovedades)} con novedades.`,
              `${numero(r.bigBags)} Big Bags (${numero(r.bigBagsNuevos)} new), ${numero(r.lotes)} lots and ${numero(r.tulas)} sacks. ${numero(r.completos)} complete and ${numero(r.incompletos)} with lots missing data. ${numero(r.bolsasConTm)} with their TM pickup (${numero(r.recoleccionesTm)} TM). ${numero(r.conNovedades)} with issues.`,
            )}
          </p>
          <p className="text-apagado">
            {Object.entries(r.porExportacion)
              .sort(([a], [b]) => a.localeCompare(b, 'es', {numeric: true}))
              .map(([k, n]) => `${k}: ${n}`)
              .join(' · ')}
          </p>
          {r.avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {r.avisos.slice(0, 20).map(a => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// El campo de archivo comun (titulo arriba, boton debajo) con la nota de ayuda
// junto al titulo. La i va fuera del texto: el campo se sigue llamando igual.
function CampoDeArchivoConAyuda({ayuda, ...props}: ComponentProps<typeof ElegirArchivo> & {ayuda: Texto}) {
  return (
    <div className="flex flex-col gap-1 text-xs text-apagado">
      <span>
        <span>{props.etiqueta}</span>
        <NotaDeAyuda titulo={props.etiqueta} texto={ayuda} />
      </span>
      <ElegirArchivo {...props} />
    </div>
  );
}

// Solo las declaraciones de valor (Daniel, 13/10/2026): el documento de transito
// con el que cada recoleccion (TM) entra a la bodega de Cartagena. Sus Big Bags
// pasan de la planta a la bodega desde la fecha de la TM, y los asignados cuya
// exportacion ya zarpo quedan exportados. Tambien se usa en Cargar archivos.
export function CargaDeDeclaracionesDeMineros({onCerrar}: {onCerrar?: () => void}) {
  const [archivos, setArchivos] = useState<File[]>([]);
  const cargar = useCargarDeclaracionesDeMineros();
  const r = cargar.data;
  const enviar = (modo: 'revisar' | 'aplicar') => archivos.length && cargar.mutate({declaraciones: archivos, modo});
  return (
    <div className="space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="carga-declaraciones">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Cargar declaraciones de valor (TM)', 'Load value declarations (TM)')}</h2>
          <p className="text-xs text-apagado">
            {tr(
              'La declaración de valor es el documento de tránsito con el que cada recolección entra a la bodega de Cartagena. Sus Big Bags pasan de la planta a la bodega desde la fecha de la TM. Los asignados a una exportación que ya zarpó quedan exportados. Se pueden elegir varias a la vez; una TM que ya estaba se reemplaza. Primero se revisa y nada se guarda hasta que confirmes.',
              'The value declaration is the transit document each pickup uses to enter the Cartagena warehouse. Its Big Bags move from the plant to the warehouse from the TM date. Those assigned to an export that already sailed become exported. You can choose several at once; a TM already loaded is replaced. It is reviewed first and nothing is saved until you confirm.',
            )}
          </p>
        </div>
        {onCerrar && (
          <button
            type="button"
            onClick={onCerrar}
            aria-label={tr('Cerrar', 'Close')}
            className="text-apagado hover:text-tinta"
          >
            ×
          </button>
        )}
      </div>
      <CampoDeArchivoConAyuda
        ayuda={AYUDA.archivoSoloDeclaraciones!}
        etiqueta={tr('Declaraciones de valor (PDF, varias)', 'Value declarations (PDF, several)')}
        accept=".pdf"
        multiple
        archivos={archivos}
        onElegir={a => {
          setArchivos(a);
          cargar.reset();
        }}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!archivos.length || cargar.isPending}
          onClick={() => enviar('revisar')}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
        >
          {tr('Revisar', 'Review')}
        </button>
        {r && !r.aplicado && (
          <button
            type="button"
            disabled={cargar.isPending}
            onClick={() => enviar('aplicar')}
            className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {tr('Confirmar y guardar', 'Confirm and save')}
          </button>
        )}
        {cargar.isPending && <span className="text-sm text-apagado">{tr('Leyendo…', 'Reading…')}</span>}
      </div>
      {cargar.isError && <p className="text-sm text-alerta">{cargar.error.message}</p>}
      {r && (
        <div className="space-y-2 text-sm" role="status">
          <p className="font-medium">
            {r.aplicado
              ? tr('Guardado.', 'Saved.')
              : tr('Revisión (todavía no se guardó nada):', 'Review (nothing saved yet):')}{' '}
            {tr(
              `${numero(r.tms.length)} TM con ${numero(r.bigBags)} Big Bags; ${numero(r.aBodega)} pasan de la planta a la bodega de Cartagena y ${numero(r.exportados)} quedan exportados porque su exportación ya zarpó.`,
              `${numero(r.tms.length)} TM with ${numero(r.bigBags)} Big Bags; ${numero(r.aBodega)} move from the plant to the Cartagena warehouse and ${numero(r.exportados)} become exported because their export already sailed.`,
            )}
          </p>
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead className="text-left text-apagado">
                <tr>
                  <th className="pr-3 font-medium">
                    TM
                    <NotaDeAyuda texto={AYUDA.tmCodigo!} />
                  </th>
                  <th className="pr-3 font-medium">
                    {tr('Fecha', 'Date')}
                    <NotaDeAyuda texto={AYUDA.tmFecha!} />
                  </th>
                  <th className="pr-3 text-right font-medium">
                    Big Bags
                    <NotaDeAyuda texto={AYUDA.tmBigBags!} />
                  </th>
                  <th className="pr-3 text-right font-medium">
                    {tr('Húmedo (kg)', 'Wet (kg)')}
                    <NotaDeAyuda texto={AYUDA.tmHumedo!} />
                  </th>
                  <th className="pr-3 text-right font-medium">
                    {tr('Valor (USD)', 'Value (USD)')}
                    <NotaDeAyuda texto={AYUDA.tmValor!} />
                  </th>
                  <th className="pr-3 text-right font-medium">
                    {tr('A bodega', 'To warehouse')}
                    <NotaDeAyuda texto={AYUDA.tmABodega!} />
                  </th>
                  <th className="pr-3 font-medium">
                    {tr('Novedad', 'Note')}
                    <NotaDeAyuda texto={AYUDA.tmNovedad!} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {r.tms.map(x => (
                  <tr key={x.tm} className="border-t border-linea">
                    <td className="pr-3 font-medium">{x.tm}</td>
                    <td className="pr-3">{x.fecha ? fecha(x.fecha) : '—'}</td>
                    <td className="pr-3 text-right tabular-nums">{numero(x.bigBags)}</td>
                    <td className="pr-3 text-right tabular-nums">{numero(Math.round(x.humedoKg))}</td>
                    <td className="pr-3 text-right tabular-nums">
                      {x.valorUsd === null ? '—' : numero(Math.round(x.valorUsd))}
                    </td>
                    <td className="pr-3 text-right tabular-nums">{numero(x.aBodega)}</td>
                    <td className="pr-3 text-apagado">
                      {[
                        x.nueva ? tr('nueva', 'new') : tr('ya estaba: se reemplaza', 'already loaded: replaced'),
                        x.noEstan.length
                          ? tr(`${x.noEstan.length} no están en Mineros`, `${x.noEstan.length} not in Mineros`)
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {r.avisos.slice(0, 20).map(a => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
